import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Building2, 
  Loader2,
  Save,
  LifeBuoy
} from 'lucide-react';
import { getEmployerProfile, updateEmployerProfile, getRescuePartner, updateRescuePartner, getAusbildungsberufe } from '../../services/api';
import { toast } from 'sonner';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';

const EmployerProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rescueData, setRescueData] = useState({
    ausbildung_rescue_partner: false,
    rescue_berufe: [],
    rescue_kapazitaet: 0,
    rescue_city: '',
    rescue_ansprechpartner: '',
    supports_language: false
  });
  const [berufe, setBerufe] = useState([]);
  const [profile, setProfile] = useState({
    company_name: '',
    industry: '',
    company_size: '',
    website: '',
    description: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Deutschland',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    logo_url: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const [res, rescueRes, berufeRes] = await Promise.all([
        getEmployerProfile(),
        getRescuePartner().catch(() => ({ data: {} })),
        getAusbildungsberufe()
      ]);
      setProfile(res.data);
      if (rescueRes.data) setRescueData(rescueRes.data);
      setBerufe(berufeRes.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const [res] = await Promise.all([
        updateEmployerProfile(profile),
        updateRescuePartner(rescueData)
      ]);
      setProfile(res.data);
      toast.success('Profil gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="employer-profile-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground">Unternehmensprofil</h1>
            <p className="text-muted-foreground mt-1">
              Verwalten Sie Ihre Unternehmensinformationen
            </p>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="rounded-full"
            data-testid="save-profile-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Speichern
          </Button>
        </div>

        {/* Company Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Unternehmensdetails
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Unternehmensname</Label>
              <Input
                id="company_name"
                value={profile.company_name}
                onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                placeholder="z.B. Tech GmbH"
                data-testid="company-name-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="industry">Branche</Label>
                <Select 
                  value={profile.industry} 
                  onValueChange={(value) => setProfile({ ...profile, industry: value })}
                >
                  <SelectTrigger data-testid="industry-select">
                    <SelectValue placeholder="Wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IT">IT & Software</SelectItem>
                    <SelectItem value="Finanzen">Finanzen & Versicherungen</SelectItem>
                    <SelectItem value="Gesundheit">Gesundheit & Pharma</SelectItem>
                    <SelectItem value="Handel">Handel & E-Commerce</SelectItem>
                    <SelectItem value="Produktion">Produktion & Industrie</SelectItem>
                    <SelectItem value="Beratung">Beratung & Consulting</SelectItem>
                    <SelectItem value="Bildung">Bildung & Forschung</SelectItem>
                    <SelectItem value="Sonstiges">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_size">Unternehmensgröße</Label>
                <Select 
                  value={profile.company_size} 
                  onValueChange={(value) => setProfile({ ...profile, company_size: value })}
                >
                  <SelectTrigger data-testid="company-size-select">
                    <SelectValue placeholder="Wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1-10 Mitarbeiter</SelectItem>
                    <SelectItem value="11-50">11-50 Mitarbeiter</SelectItem>
                    <SelectItem value="51-200">51-200 Mitarbeiter</SelectItem>
                    <SelectItem value="201-500">201-500 Mitarbeiter</SelectItem>
                    <SelectItem value="500+">500+ Mitarbeiter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={profile.website}
                onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                placeholder="https://www.beispiel.de"
                data-testid="website-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Unternehmensbeschreibung</Label>
              <Textarea
                id="description"
                value={profile.description}
                onChange={(e) => setProfile({ ...profile, description: e.target.value })}
                placeholder="Beschreiben Sie Ihr Unternehmen..."
                rows={4}
                data-testid="description-textarea"
              />
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card>
          <CardHeader>
            <CardTitle>Adresse</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="address">Straße und Hausnummer</Label>
              <Input
                id="address"
                value={profile.address}
                onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                placeholder="z.B. Musterstraße 123"
                data-testid="address-input"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postal_code">PLZ</Label>
                <Input
                  id="postal_code"
                  value={profile.postal_code}
                  onChange={(e) => setProfile({ ...profile, postal_code: e.target.value })}
                  data-testid="postal-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Stadt</Label>
                <Input
                  id="city"
                  value={profile.city}
                  onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                  data-testid="city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Land</Label>
                <Input
                  id="country"
                  value={profile.country}
                  onChange={(e) => setProfile({ ...profile, country: e.target.value })}
                  data-testid="country-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader>
            <CardTitle>Ansprechpartner</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="contact_person">Name</Label>
              <Input
                id="contact_person"
                value={profile.contact_person}
                onChange={(e) => setProfile({ ...profile, contact_person: e.target.value })}
                placeholder="z.B. Max Mustermann"
                data-testid="contact-person-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_email">E-Mail</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={profile.contact_email}
                  onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })}
                  placeholder="hr@beispiel.de"
                  data-testid="contact-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Telefon</Label>
                <Input
                  id="contact_phone"
                  value={profile.contact_phone}
                  onChange={(e) => setProfile({ ...profile, contact_phone: e.target.value })}
                  placeholder="+49 123 456789"
                  data-testid="contact-phone-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rescue Partner */}
        <Card className={rescueData.ausbildung_rescue_partner ? 'border-primary/30' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <LifeBuoy className="w-5 h-5 text-primary" />
                Ausbildungs-Rescue Partner
              </span>
              <Switch
                checked={rescueData.ausbildung_rescue_partner}
                onCheckedChange={(v) => setRescueData({ ...rescueData, ausbildung_rescue_partner: v })}
              />
            </CardTitle>
          </CardHeader>
          {rescueData.ausbildung_rescue_partner && (
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Als Rescue-Partner bieten Sie Ausbildungsplätze für Azubis an, die ihren Betrieb wechseln müssen.
              </p>
              <div className="space-y-2">
                <Label>Ausbildungsberufe</Label>
                <div className="grid grid-cols-2 gap-2">
                  {berufe.map(b => (
                    <div
                      key={b.key}
                      className={`p-3 rounded-lg border cursor-pointer text-sm transition-all ${
                        rescueData.rescue_berufe.includes(b.key)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => {
                        const updated = rescueData.rescue_berufe.includes(b.key)
                          ? rescueData.rescue_berufe.filter(x => x !== b.key)
                          : [...rescueData.rescue_berufe, b.key];
                        setRescueData({ ...rescueData, rescue_berufe: updated });
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox checked={rescueData.rescue_berufe.includes(b.key)} className="pointer-events-none" />
                        <span>{b.name}</span>
                      </div>
                      <Badge variant="outline" className="mt-1 text-xs">{b.kammer}</Badge>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Kapazität (Plätze)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={rescueData.rescue_kapazitaet}
                    onChange={(e) => setRescueData({ ...rescueData, rescue_kapazitaet: parseInt(e.target.value) || 0 })}
                    data-testid="rescue-capacity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ansprechpartner</Label>
                  <Input
                    value={rescueData.rescue_ansprechpartner}
                    onChange={(e) => setRescueData({ ...rescueData, rescue_ansprechpartner: e.target.value })}
                    placeholder="Name Ansprechpartner"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <p className="font-medium text-sm">Sprachförderung möglich</p>
                  <p className="text-xs text-muted-foreground">Bieten Sie Sprachunterstützung für Azubis?</p>
                </div>
                <Switch
                  checked={rescueData.supports_language}
                  onCheckedChange={(v) => setRescueData({ ...rescueData, supports_language: v })}
                />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Save Button (Mobile) */}
        <div className="md:hidden">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="rounded-full w-full"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Speichern
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default EmployerProfile;
